import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtINR, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { TrendUp, TrendDown, FilmSlate, Package as PackageIcon, Warning, Circle, ArrowRight } from "@phosphor-icons/react";

function KPI({ label, value, sub, tone = "neutral", "data-testid": testId }) {
  const tones = {
    neutral: "text-white",
    success: "text-emerald-400",
    warning: "text-amber-400",
    danger: "text-red-400",
    accent: "text-[#D97706]",
  };
  return (
    <div data-testid={testId} className="hairline rounded-lg p-4 bg-[#0F0F0F] hover:bg-[#131313] transition-colors">
      <div className="text-[10px] uppercase tracking-widest text-white/40">{label}</div>
      <div className={`mono text-2xl font-bold mt-2 ${tones[tone]}`}>{value}</div>
      {sub && <div className="text-[11px] text-white/40 mt-1">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="p-10 text-white/40">Loading dashboard…</div>;
  const s = data.stats;

  return (
    <>
      <PageHeader title="Ops Dashboard" subtitle="Real-time snapshot of your production house" />
      <div className="p-8 space-y-6 fade-up">
        {/* Top KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI data-testid="kpi-active-projects" label="Active Projects" value={s.active_projects} sub={`${s.completed_projects} completed`} />
          <KPI data-testid="kpi-revenue" label="Revenue Received" value={fmtINR(s.revenue)} sub={`of ${fmtINR(s.invoiced)} invoiced`} tone="success" />
          <KPI data-testid="kpi-profit" label="Net Profit" value={fmtINR(s.profit)} sub="After crew + expenses" tone={s.profit >= 0 ? "success" : "danger"} />
          <KPI data-testid="kpi-client-pending" label="Client Pending" value={fmtINR(s.client_pending)} sub="To be collected" tone="warning" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI label="Upcoming Shoots" value={s.upcoming_shoots} tone="accent" />
          <KPI label="Upcoming Deliveries" value={s.upcoming_deliveries} />
          <KPI label="Freelancer Payouts Pending" value={fmtINR(s.freelancer_pending)} tone="warning" />
          <KPI label="Open Tasks" value={s.open_tasks} />
        </div>

        {/* Main grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Upcoming shoots */}
          <div className="lg:col-span-8 hairline rounded-lg bg-[#0F0F0F]">
            <div className="p-4 hairline-b flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold">Upcoming Shoots</div>
                <div className="text-[11px] text-white/40">Next {data.upcoming_shoots.length} scheduled</div>
              </div>
              <Link to="/projects" className="text-[11px] text-white/50 hover:text-white flex items-center gap-1">All projects <ArrowRight size={12} /></Link>
            </div>
            <div className="divide-y divide-white/5">
              {data.upcoming_shoots.length === 0 && <div className="p-6 text-white/40 text-sm">No upcoming shoots.</div>}
              {data.upcoming_shoots.map((p) => (
                <Link key={p.id} to={`/projects/${p.id}`} data-testid={`upcoming-shoot-${p.id}`} className="flex items-center gap-4 p-4 hover:bg-white/[0.02] transition-colors">
                  <FilmSlate size={18} weight="duotone" className="text-[#D97706]" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{p.name}</div>
                    <div className="text-[11px] text-white/40 truncate">{p.client_name} · {p.location || "—"} · Call {p.call_time || "TBD"}</div>
                  </div>
                  <div className="mono text-xs text-white/70">{fmtDate(p.shoot_date)}</div>
                </Link>
              ))}
            </div>
          </div>

          {/* Activity feed */}
          <div className="lg:col-span-4 hairline rounded-lg bg-[#0F0F0F]">
            <div className="p-4 hairline-b">
              <div className="text-sm font-semibold">Recent Activity</div>
              <div className="text-[11px] text-white/40">System log</div>
            </div>
            <div className="p-4 space-y-3 max-h-[280px] overflow-y-auto">
              {data.activities.length === 0 && <div className="text-white/40 text-sm">No activity yet.</div>}
              {data.activities.map((a) => (
                <div key={a.id} className="text-xs">
                  <div className="flex items-center gap-2">
                    <Circle size={6} weight="fill" className="text-[#D97706]" />
                    <span className="text-white/70">{a.action} {a.entity}</span>
                    <span className="text-white/40 mono text-[10px] ml-auto">{fmtDate(a.created_at)}</span>
                  </div>
                  {a.meta?.name && <div className="ml-4 text-white/90 truncate">{a.meta.name}</div>}
                </div>
              ))}
            </div>
          </div>

          {/* Upcoming deliveries */}
          <div className="lg:col-span-6 hairline rounded-lg bg-[#0F0F0F]">
            <div className="p-4 hairline-b">
              <div className="text-sm font-semibold">Upcoming Deliveries</div>
            </div>
            <div className="divide-y divide-white/5">
              {data.upcoming_deliveries.map((p) => (
                <Link key={p.id} to={`/projects/${p.id}`} className="flex items-center gap-4 p-3 hover:bg-white/[0.02]">
                  <PackageIcon size={16} weight="duotone" className="text-emerald-400" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{p.name}</div>
                    <div className="text-[10px] text-white/40">{p.client_name}</div>
                  </div>
                  <div className="mono text-xs text-white/70">{fmtDate(p.delivery_date)}</div>
                </Link>
              ))}
              {data.upcoming_deliveries.length === 0 && <div className="p-6 text-white/40 text-sm">Nothing scheduled.</div>}
            </div>
          </div>

          {/* Crew availability */}
          <div className="lg:col-span-6 hairline rounded-lg bg-[#0F0F0F]">
            <div className="p-4 hairline-b">
              <div className="text-sm font-semibold">Crew Availability · This Week</div>
            </div>
            <div className="p-4 grid grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-emerald-400/70 mb-2">Available</div>
                <div className="space-y-1.5">
                  {data.crew_availability.available.map(f => (
                    <div key={f.id} className="text-xs text-white/80 flex items-center gap-2">
                      <Circle size={6} weight="fill" className="text-emerald-400" />{f.name}
                    </div>
                  ))}
                  {data.crew_availability.available.length === 0 && <div className="text-white/40 text-xs">Everyone busy.</div>}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-amber-400/70 mb-2">Booked</div>
                <div className="space-y-1.5">
                  {data.crew_availability.busy.map(f => (
                    <div key={f.id} className="text-xs text-white/80 flex items-center gap-2">
                      <Circle size={6} weight="fill" className="text-amber-400" />{f.name}
                    </div>
                  ))}
                  {data.crew_availability.busy.length === 0 && <div className="text-white/40 text-xs">All clear.</div>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
